import { useEffect, useState, useRef } from 'react';
import { clearPersistentCache } from '../lib/api';
import ViewModeToggle from './ViewModeToggle';
import WelcomeToggle from './WelcomeToggle';
import { visibleLayoutOptions, layoutKeyForRole, setLayoutForRole } from '../lib/layoutTheme';

// Persisted in localStorage `3c_theme`. The pre-paint script in
// public/index.html already applies the class on <html> before first
// paint; this hook keeps state in sync after mount.
export function useTheme() {
  const [theme, setTheme] = useState(() =>
    localStorage.getItem('3c_theme') === 'light' ? 'light' : 'dark'
  );
  useEffect(() => {
    document.documentElement.classList.toggle('theme-light', theme === 'light');
    document.body.classList.toggle('theme-light', theme === 'light');
    try { localStorage.setItem('3c_theme', theme); } catch (e) {}
  }, [theme]);
  return [theme, setTheme];
}

// Returns an onClick handler that fires `onTrigger` on the 2nd tap
// within 500 ms. Used by the 3CHA logo on Dashboard + Detailer headers.
export function useDoubleTap(onTrigger) {
  const clicks = useRef(0);
  const timer = useRef(null);
  return function onClick() {
    clicks.current += 1;
    if (clicks.current >= 2) {
      clicks.current = 0;
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      onTrigger();
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { clicks.current = 0; }, 500);
  };
}

// Hard-refresh control. Mirrors the inline `.live-indicator` on
// Dashboard + Detailer: pulsing green dot + "Live" label, click busts
// the client cache and reloads with a `_t=<now>` URL param so
// aggressive browser/proxy caching can't serve a stale page. Pages
// that don't already ship their own indicator (Marketing, Books,
// Booking) drop this in.
// TRUE hard refresh — reset EVERYTHING short of the login session, then reload
// so the page comes back entirely from the network. Stronger than Cmd+Shift+R:
//   1. Drop the persisted in-memory-cache snapshot (localStorage).
//   2. Delete every Cache Storage bucket (the app shell + all hashed assets).
//   3. FULLY UNREGISTER every service worker — not just skip-waiting the pending
//      one. This is the piece a plain reload (and the old skipWaiting-only path)
//      missed: while the active SW stays registered it can keep serving a stale
//      cached shell/chunks. Unregistered, the reload pulls index.html + every
//      chunk straight from the network; index.html re-registers a fresh SW on
//      the way back in.
//   4. Reload with a cache-bust `_t` param so no HTTP/proxy layer serves a stale
//      document either.
// Login session (localStorage) + view state (sessionStorage) are left intact —
// this refreshes the CODE + DATA, it doesn't log the user out or reset their tab.
export async function hardReload() {
  try { clearPersistentCache(); } catch (_) {}
  try {
    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (_) {}
  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch (_) {}
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('_t', String(Date.now()));
    window.location.replace(u.toString());
  } catch (_) {
    window.location.reload();
  }
}

export function LiveRefreshButton({ onRefresh, title = 'Click for hard refresh' }) {
  function handle() {
    try { if (typeof onRefresh === 'function') onRefresh(); } catch (_) {}
    hardReload();
  }
  return (
    <div onClick={handle} title={title}
      style={{display:'flex',alignItems:'center',gap:5,fontSize:11,color:'var(--muted, gray)',fontWeight:500,letterSpacing:'.3px',cursor:'pointer',userSelect:'none'}}>
      <span style={{width:7,height:7,background:'var(--green, #10b981)',borderRadius:'50%',animation:'pulse 2s infinite',display:'inline-block'}}/>
      Live
    </div>
  );
}

// Skin (layout) picker for the double-tap modal — same 7 skins the owner/
// principal get in their Background, offered to the detailer here. Persists
// per-role and applies live; doesn't close the modal so skins can be compared.
function ModalLayoutPicker({ role }) {
  const key = layoutKeyForRole(role);
  const [current, setCurrent] = useState(() => { try { return localStorage.getItem(key) || ''; } catch (_) { return ''; } });
  function pick(id) { setLayoutForRole(role, id); setCurrent(id); }
  return (
    <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid var(--border)'}}>
      <div style={{fontSize:11,fontWeight:700,color:'var(--muted)',letterSpacing:0.8,textTransform:'uppercase',marginBottom:12}}>Skin</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(118px,1fr))',gap:8}}>
        {visibleLayoutOptions(role).map((opt) => {
          const active = current === opt.id;
          return (
            <button key={opt.id || 'default'} onClick={() => pick(opt.id)}
              style={{padding:'9px 10px',border:'1px solid '+(active?'var(--blue)':'var(--border)'),background:active?'var(--blue-dim, rgba(37,99,235,0.12))':'transparent',color:'var(--text)',borderRadius:10,cursor:'pointer',textAlign:'left',fontFamily:'inherit'}}>
              <div style={{fontWeight:700,fontSize:12.5,marginBottom:2}}>{opt.label}{active && <span style={{color:'var(--blue)',fontSize:9,marginLeft:4}}>● ON</span>}</div>
              <div style={{color:'var(--muted)',fontSize:10.5,lineHeight:1.3}}>{opt.blurb}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// `viewRole` (optional) — the Detailer passes their role so the modal (opened by
// double-tapping the 3CHA logo) also carries the detailer's personal settings:
// the Skin picker + Simple/Advanced view + Sign-in Welcome toggles.
export function ThemePickerModal({ open, onClose, theme, setTheme, viewRole = null }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:9500,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:14,padding:20,minWidth:260,maxWidth:340,maxHeight:'85vh',overflowY:'auto',boxShadow:'var(--shadow)'}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--muted)',letterSpacing:0.8,textTransform:'uppercase',marginBottom:12}}>Theme</div>
        <div style={{display:'flex',gap:10}}>
          <button onClick={()=>{setTheme('dark'); if(!viewRole) onClose();}} style={{flex:1,padding:'12px 14px',borderRadius:10,border:`1px solid ${theme==='dark'?'var(--blue)':'var(--border)'}`,background:theme==='dark'?'var(--blue-dim)':'transparent',color:'var(--text)',fontSize:13,fontWeight:600,cursor:'pointer'}}>🌙 Dark</button>
          <button onClick={()=>{setTheme('light'); if(!viewRole) onClose();}} style={{flex:1,padding:'12px 14px',borderRadius:10,border:`1px solid ${theme==='light'?'var(--blue)':'var(--border)'}`,background:theme==='light'?'var(--blue-dim)':'transparent',color:'var(--text)',fontSize:13,fontWeight:600,cursor:'pointer'}}>☀️ Light</button>
        </div>
        {viewRole && <ModalLayoutPicker role={viewRole} />}
        {viewRole && (
          <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid var(--border)'}}>
            <ViewModeToggle role={viewRole} heading={
              <div style={{fontSize:11,fontWeight:700,color:'var(--muted)',letterSpacing:0.8,textTransform:'uppercase',margin:'0 0 12px'}}>View</div>
            } />
          </div>
        )}
        {viewRole && (
          <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid var(--border)'}}>
            <WelcomeToggle role={viewRole} heading={
              <div style={{fontSize:11,fontWeight:700,color:'var(--muted)',letterSpacing:0.8,textTransform:'uppercase',margin:'0 0 12px'}}>Sign-in Welcome</div>
            } />
          </div>
        )}
      </div>
    </div>
  );
}
